import React, { useEffect, useState, useCallback } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import {
  Box,
  Grid,
  IconButton,
  makeStyles,
  TextField,
} from "@material-ui/core/";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import Tooltip from "@material-ui/core/Tooltip";
import { Link } from "react-router-dom";
import DnerhsApi, { CancelToken } from "../../api/DnerhsApi";
import Fab from "@material-ui/core/Fab";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import LockIcon from "@material-ui/icons/Lock";
import useAuth from "../../hooks/Auth";
import Roles from "../../constants/Roles";
import DeleteDialog from "../../dialogs/DeleteDialog";
import CambiarPasswordDialog from "../../components/CambiarPasswordDialog";
import Alert from "../../components/Alert";
import ResponseUtils from "../../utils/ResponseUtils";
import DeleteIcon from "@material-ui/icons/Delete";
import { debounce } from "lodash";

const useStyles = makeStyles((theme) => ({
  title: {
    margin: theme.spacing(3),
  },
  button: {
    margin: theme.spacing(),
  },
  sortable: {
    cursor: 'pointer',
  },
  tableContainer: {
    overflowX: 'auto', // Agregar desplazamiento horizontal para la tabla
  },
  searchField: {
    width: '100%', // Hacer que el campo de búsqueda ocupe el 100%
    [theme.breakpoints.up('sm')]: {
      width: '30%', // Ancho del 30% en pantallas más grandes
    },
  },
}));

const List = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const { hasRole } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [toDelete, setToDelete] = useState();
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("username"); // Columna por la que se ordena
  const [sortDirection, setSortDirection] = useState("asc"); // Dirección de ordenamiento

  // Estados para cambio de contraseña
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState(null);

  useEffect(() => {
    loadData(); // Cargar datos inicialmente
  }, [page, rowsPerPage, searchTerm, sortColumn, sortDirection]); // Dependencias que desencadenan la carga de datos

  const loadData = async () => {
    const source = CancelToken.source();
    try {
      const url = `/usuarios/page?page=${page}&pageSize=${rowsPerPage}&search=${searchTerm}&sort=${sortColumn},${sortDirection}`;
      console.log("Loading data from URL:", url); // Agrega este console.log
      const response = await DnerhsApi.get(url, {
        cancelToken: source.token,
      });

      if (response.status === 200) {
        const rowsData = response.data;
        setRows(rowsData.content || []);
        setTotalElements(rowsData.totalElements || 0);
      } else {
        console.error("Error al obtener datos de la API.");
      }
    } catch (error) {
      console.log(error);
      showServerError(error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteOne = async () => {
    const source = CancelToken.source();
    try {
      const url = `/usuarios/${toDelete}`;
      await DnerhsApi.delete(url, {
        cancelToken: source.token,
      });

      Alert.show({ message: "Eliminado exitosamente.", type: "success" });
      await loadData(); // Recargar datos después de eliminar
    } catch (error) {
      console.log(error);
      showServerError(error);
    } finally {
      setOpenDialog(false);
      setToDelete(undefined);
    }
  };

  const handleOpenDialog = (id) => {
    setOpenDialog(true);
    setToDelete(id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Funciones para cambio de contraseña
  const handleOpenPasswordDialog = (user) => {
    setUserToChangePassword(user);
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setUserToChangePassword(null);
  };

  const handleChangePassword = async (newPassword) => {
    try {
      const url = `/usuarios/${userToChangePassword.id}/cambiar-password-admin`;
      await DnerhsApi.post(url, {
        password: newPassword,
        username: userToChangePassword.username
      });

      Alert.show({
        message: `Contraseña actualizada exitosamente para ${userToChangePassword.username}`,
        type: "success"
      });

      handleClosePasswordDialog();
    } catch (error) {
      console.log(error);
      showServerError(error);
    }
  };

  const showServerError = (error) => {
    let message = "Ocurrió un error inesperado, por favor vuelva a reintentar";
    if (error.response) {
      message = ResponseUtils.getMessageError(error.response);
    }

    Alert.show({
      message,
      type: "error",
    });
  };

  // Usar useCallback para envolver la función de búsqueda
  const debouncedSearchTerm = useCallback(
    debounce((term) => {
      setSearchTerm(term);
      setPage(0); // Reiniciar a la primera página en cada nueva búsqueda
    }, 300),
    []
  );

  const handleSearch = (event) => {
    const { value } = event.target;
    debouncedSearchTerm(value);
  };

  // Función para manejar el clic en los encabezados de las columnas
  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortColumn(column);
    setSortDirection(isAsc ? "desc" : "asc"); // Cambia la dirección
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Paper>
          <Box p={1}>
            <Typography
              variant="h5"
              color="primary"
              align="center"
              className={classes.title}
            >
              Usuarios
            </Typography>
            {/* Barra de búsqueda */}
            <TextField
              label="Buscar"
              variant="outlined"
              margin="normal"
              onChange={handleSearch} // Llama a handleSearch en el cambio
              className={classes.searchField} // Usar la clase para el campo de búsqueda
            />

            <div className={classes.tableContainer}> {/* Contenedor para la tabla */}
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.sortable} onClick={() => handleSort('username')}>Nombre de Usuario</TableCell>
                    <TableCell className={classes.sortable} onClick={() => handleSort('nombres')}>Nombres</TableCell>
                    <TableCell className={classes.sortable} onClick={() => handleSort('apellidos')}>Apellidos</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.username}</TableCell>
                      <TableCell>{row.nombres}</TableCell>
                      <TableCell>{row.apellidos}</TableCell>
                      <TableCell align="center">
                        {hasRole(Roles.ROLE_DNERHS) && (
                          <>
                            <Link
                              to={{
                                pathname: `/usuarios/editar/${row.id}`,
                                state: { edit: true },
                              }}
                            >
                              <Tooltip title="Editar" aria-label="editar">
                                <IconButton size="small" aria-label="editar" color="primary">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            </Link>

                            <Tooltip title="Cambiar Contraseña" aria-label="cambiar-password">
                              <IconButton
                                size="small"
                                aria-label="cambiar-password"
                                style={{ color: '#ff9800' }}
                                onClick={() => handleOpenPasswordDialog(row)}
                              >
                                <LockIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar" aria-label="eliminar">
                              <IconButton
                                size="small"
                                aria-label="eliminar"
                                color="secondary"
                                onClick={() => handleOpenDialog(row.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      count={totalElements}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </Box>
        </Paper>
      </Grid>
      {
        hasRole(Roles.ROLE_DNERHS) &&
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid item className={classes.button}>
            <Link to="/usuarios/crear">
              <Fab color="primary" aria-label="nuevo">
                <Tooltip title="Nueva Usuario" aria-lafbel="nuevo">
                  <AddIcon />
                </Tooltip>
              </Fab>
            </Link>
          </Grid>
        </Grid>
      }
     
      {/* Diálogo de confirmación para eliminar */}
      <DeleteDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        onDelete={deleteOne}
      />

      {/* Diálogo para cambiar contraseña */}
      <CambiarPasswordDialog
        open={openPasswordDialog}
        onClose={handleClosePasswordDialog}
        user={userToChangePassword}
        onChangePassword={handleChangePassword}
      />
    </Grid>
  );
};

export default List;
